import { localizeDateNames } from "@/lib/dateNames";
import i18n from "@/i18n";
/**
 * Prompt 16B-2B1 · Transferred X Schedule UX — compact popover.
 *
 * Presented next to an existing X Day Entry cell whose employee is
 * on AVERAGING + TES12_1_STANDARD. Emits a single `update-day-entry`
 * intent carrying `x_transfer_source_period_id` +
 * `x_transfer_averaging_cycle_id` (both non-null → transferred X,
 * both null → cleared to ordinary X). Does NOT redo backend
 * business validation — surfaces friendly copy on canonical
 * `WORKFORCE_X_TRANSFER_*` error codes through the existing toast
 * pipeline in WorkforceSchedule.
 *
 * §4 · §7 — technical IDs are never rendered. The dropdown shows
 * human-readable date ranges only; period_ids stay opaque.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

import { api } from "@/lib/api";

// §4 canonical 21-day period width. TES12_1_STANDARD only.
const _PERIOD_DAYS = 21;

function _addDays(iso, days) {
    // Prompt 16B-2B1 §4 — pure ISO-date math; no Date TZ ambiguity.
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + days));
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
}

function _humanRange(startIso, endIso) {
    // Compact "14 Sep – 4 Oct" style (§4 example).
    const fmt = (iso) => {
        const [y, m, d] = iso.split("-").map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        return localizeDateNames(dt.toLocaleDateString("en-GB",
            { day: "numeric", month: "short", timeZone: "UTC" }));
    };
    return `${fmt(startIso)} – ${fmt(endIso)}`;
}

function _deriveCyclePeriods(cycleStart, cycleEnd) {
    // Prompt 16B-2B1 §4 — canonical 21-day windows starting at the
    // cycle start (guaranteed Monday by 16A). Yields the list of
    // (start, end) tuples fully inside the cycle.
    if (!cycleStart || !cycleEnd) return [];
    const out = [];
    let idx = 0;
    let cur = cycleStart;
    while (cur <= cycleEnd) {
        const end = _addDays(cur, _PERIOD_DAYS - 1);
        if (end > cycleEnd) break;
        out.push({ start: cur, end, index: idx });
        cur = _addDays(cur, _PERIOD_DAYS);
        idx += 1;
    }
    return out;
}

/**
 * §3 · §4 · §5 compact popover. Only rendered by the caller when
 * the employee is on AVERAGING + TES12_1_STANDARD.
 */
export function TransferredXControl({
    restaurantId,
    dayEntry,
    cycle,
    onIntent,
    isPending,
}) {
    const initialSrc = dayEntry?.x_transfer_source_period_id || null;
    const initialCyc = dayEntry?.x_transfer_averaging_cycle_id || null;
    const initialOn = Boolean(initialSrc && initialCyc);

    const [open, setOpen] = useState(false);
    const [transferOn, setTransferOn] = useState(initialOn);
    const [selectedStart, setSelectedStart] = useState(null);
    const [resolvedSources, setResolvedSources] = useState({});
    const [resolveErr, setResolveErr] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => () => { mountedRef.current = false; }, []);

    // Reset local state when the underlying Day Entry identity
    // changes. Keeps stale UI from carrying across cell edits.
    useEffect(() => {
        setTransferOn(initialOn);
        setSelectedStart(null);
        setResolvedSources({});
        setResolveErr(false);
    }, [dayEntry?.day_entry_id, initialOn]);

    const cyclePeriods = useMemo(
        () => _deriveCyclePeriods(cycle?.start_date, cycle?.end_date),
        [cycle?.start_date, cycle?.end_date]);

    // §4 — destination period is excluded. Compute destination
    // canonical start from the Day Entry date.
    const destCanonicalStart = useMemo(() => {
        if (!cycle?.start_date || !dayEntry?.date) return null;
        // Find the canonical period the date falls into.
        const anchor = cycle.start_date;
        const [ay, am, ad] = anchor.split("-").map(Number);
        const [dy, dm, dd] = dayEntry.date.split("-").map(Number);
        const anchorMs = Date.UTC(ay, am - 1, ad);
        const dateMs = Date.UTC(dy, dm - 1, dd);
        const days = Math.floor((dateMs - anchorMs) / 86400000);
        const n = Math.floor(days / _PERIOD_DAYS);
        return _addDays(anchor, n * _PERIOD_DAYS);
    }, [cycle?.start_date, dayEntry?.date]);

    const sourceOptions = useMemo(
        () => cyclePeriods.filter((p) => p.start !== destCanonicalStart),
        [cyclePeriods, destCanonicalStart]);

    // Preselect the currently-persisted source (by matching one of
    // the derived start dates).
    useEffect(() => {
        if (!open) return;
        if (selectedStart !== null) return;
        if (!initialSrc) {
            // Default to the first eligible option so a fresh
            // enable is one click.
            if (sourceOptions.length > 0) {
                setSelectedStart(sourceOptions[0].start);
            }
            return;
        }
        // Try to match by reverse-lookup: any resolved source that
        // maps to initialSrc.
        for (const [start, pid] of Object.entries(resolvedSources)) {
            if (pid === initialSrc) {
                setSelectedStart(start);
                return;
            }
        }
        // Best-effort: fall back to first option.
        if (sourceOptions.length > 0) {
            setSelectedStart(sourceOptions[0].start);
        }
    }, [
        open, sourceOptions, initialSrc, selectedStart,
        resolvedSources,
    ]);

    // When the user toggles ON in an already-open popover, ensure
    // a default source is selected synchronously so the Save button
    // is immediately actionable without waiting for a second render.
    function _handleToggleChange(checked) {
        setTransferOn(checked);
        if (checked && !selectedStart && sourceOptions.length > 0) {
            setSelectedStart(sourceOptions[0].start);
        }
    }

    // Resolve period_ids for the derived starts on-demand. §14 —
    // reuses the existing GET /periods/current endpoint; no new
    // endpoint is introduced.
    async function _resolvePeriodId(startIso) {
        if (resolvedSources[startIso]) return resolvedSources[startIso];
        try {
            const resp = await api.get(
                "/workforce/periods/current",
                { params: { restaurant_id: restaurantId, date: startIso } },
            );
            const pid = resp?.data?.period_id;
            if (pid && mountedRef.current) {
                setResolvedSources((prev) => ({ ...prev, [startIso]: pid }));
                return pid;
            }
        } catch (_e) {
            if (mountedRef.current) setResolveErr(true);
        }
        return null;
    }

    async function _handleSave() {
        if (isPending) return;
        if (!transferOn) {
            // §5 — turn OFF clears provenance via update-day-entry
            // with both fields explicitly null.
            onIntent?.({
                mode: "update-day-entry",
                code: "X",
                dayEntry,
                navigate: null,
                source: "transferred-x-control",
                x_transfer_source_period_id: null,
                x_transfer_averaging_cycle_id: null,
            });
            setOpen(false);
            return;
        }
        if (!selectedStart) return;
        const pid = await _resolvePeriodId(selectedStart);
        if (!pid) return;
        onIntent?.({
            mode: "update-day-entry",
            code: "X",
            dayEntry,
            navigate: null,
            source: "transferred-x-control",
            x_transfer_source_period_id: pid,
            x_transfer_averaging_cycle_id: cycle.averaging_cycle_id,
        });
        setOpen(false);
    }

    const testidPrefix =
        `transferred-x-${dayEntry?.day_entry_id || "unknown"}`;

    return (
        <span className="relative inline-block">
            <button
                type="button"
                aria-label={initialOn
                    ? i18n.t("wf.transferredXControl.transferredXDetails", "Transferred X details")
                    : i18n.t("wf.transferredXControl.enableTransferredX", "Enable transferred X")}
                title={initialOn ? i18n.t("wf.transferredXControl.transferred", "Transferred") : i18n.t("wf.transferredXControl.transferredX", "Transferred X")}
                data-testid={`${testidPrefix}-trigger`}
                onClick={() => setOpen((o) => !o)}
                className="ml-1 text-[10px] leading-4 text-neutral-500
                            hover:text-neutral-800 focus:outline-none
                            focus-visible:ring-1
                            focus-visible:ring-neutral-400 rounded px-0.5"
            >
                {initialOn ? "T" : "…"}
            </button>
            {open ? (
                <div
                    data-testid={`${testidPrefix}-popover`}
                    className="absolute z-50 left-0 top-full mt-1 w-64
                                bg-white border border-neutral-200
                                shadow-md rounded p-3 text-sm text-left"
                >
                    <label className="flex items-center gap-2 mb-2">
                        <input
                            type="checkbox"
                            checked={transferOn}
                            onChange={(e) =>
                                _handleToggleChange(e.target.checked)}
                            data-testid={`${testidPrefix}-toggle`}
                        />
                        <span>{i18n.t("wf.transferredXControl.transferredX", "Transferred X")}</span>
                    </label>
                    {transferOn ? (
                        <>
                            <div className="text-xs text-neutral-600 mb-1">
                                {i18n.t("wf.transferredXControl.fromPeriod", "From period")}
                            </div>
                            <select
                                value={selectedStart || ""}
                                onChange={(e) =>
                                    setSelectedStart(e.target.value)}
                                data-testid={`${testidPrefix}-source`}
                                className="w-full border rounded px-2 py-1 text-sm"
                            >
                                {sourceOptions.map((p) => (
                                    <option key={p.start} value={p.start}>
                                        {_humanRange(p.start, p.end)}
                                    </option>
                                ))}
                            </select>
                        </>
                    ) : null}
                    {resolveErr ? (
                        <div
                            className="text-xs text-red-600 mt-2"
                            data-testid={`${testidPrefix}-resolve-error`}
                        >
                            {i18n.t("wf.transferredXControl.couldNotLoadPeriod", "Could not load period.")}
                        </div>
                    ) : null}
                    <div className="mt-3 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid={`${testidPrefix}-cancel`}
                            className="text-xs text-neutral-600 px-2 py-1"
                        >
                            {i18n.t("common.cancel", "Cancel")}
                        </button>
                        <button
                            type="button"
                            onClick={_handleSave}
                            disabled={isPending
                                || (transferOn && !selectedStart)}
                            data-testid={`${testidPrefix}-save`}
                            className="text-xs bg-neutral-900 text-white
                                        rounded px-2 py-1 disabled:opacity-50"
                        >
                            {i18n.t("common.save", "Save")}
                        </button>
                    </div>
                </div>
            ) : null}
        </span>
    );
}

export default TransferredXControl;
